import { OrganizationSwitcher, UserButton } from "@clerk/clerk-react";
import {
  AdsClick as AdsClickIcon,
  BugReport as BugReportIcon,
  ChevronLeft as CollapseIcon,
  Computer as ComputerIcon,
  ChevronRight as ExpandIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Home as HomeIcon,
  JoinFull as JoinFullIcon,
  LiveHelp as LiveHelpIcon,
  Menu as MenuIcon,
  QueryStats as MilitaryTechIcon,
  MonitorHeart as MonitorHeartIcon,
  School as OnboardingIcon,
  Schedule as ScheduleIcon,
  Science as ScienceIcon,
  Settings as SettingsIcon,
  Subscriptions as SubscriptionsIcon,
  TravelExplore as TravelExploreIcon,
  AutoAwesome as VibeIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSupportChatContext } from "../../contexts/support-chat-context";
import { useApiQuery } from "../../hooks";
import { useOrgFlags } from "../../hooks/use-org-flags";
import { BRAND_PRIMARY, SIDEBAR } from "../../theme/theme";
import { clickableProps } from "../../utils/keyboard";
import { DespleaIcon } from "../icons/desplega-icon";
import { UsageInfoStack } from "../usage-info-stack";

export const SIDEBAR_EXPANDED_WIDTH = 200;
export const SIDEBAR_COLLAPSED_WIDTH = 56;

// Marker title for the onboarding nav entry — the navigation renderer
// filters this entry out when `flags.onboarding.completed_at` is set.
// Kept in NAVIGATION (rather than injected at render time) so the order
// relative to "Home" stays declaratively fixed at the top of the array.
const ONBOARDING_NAV_TITLE = "Onboarding";

// Original navigation structure from App.tsx
const NAVIGATION = [
  {
    title: "Home",
    href: "/",
    icon: HomeIcon,
  },
  {
    title: ONBOARDING_NAV_TITLE,
    href: "/onboarding",
    icon: OnboardingIcon,
    highlight: true,
  },
  {
    title: "Analytics",
    href: "/analytics",
    icon: MilitaryTechIcon,
  },
  {
    title: "Issues",
    href: "/issues",
    icon: BugReportIcon,
  },
  {
    title: "Argus",
    href: "/argus",
    icon: DespleaIcon,
    badge: "BETA",
  },
  {
    kind: "header",
    title: "E2E Tests",
  },
  {
    title: "Creation",
    icon: AdsClickIcon,
    children: [
      {
        title: "New Test",
        href: "/generate",
        icon: AdsClickIcon,
      },
      {
        title: "Discovery",
        href: "/discovery-runs",
        icon: TravelExploreIcon,
      },
      {
        title: "MCP",
        href: "/vibe-qa",
        icon: VibeIcon,
      },
    ],
  },
  {
    title: "Tests",
    href: "/test-cases",
    icon: ScienceIcon,
    children: [
      {
        title: "Test Runs",
        href: "/runs",
        icon: SubscriptionsIcon,
      },
    ],
  },
  {
    title: "Suites",
    href: "/tests-main/test-suites",
    icon: FolderIcon,
    children: [
      {
        title: "Suite Runs",
        href: "/suite-runs",
        icon: JoinFullIcon,
      },
    ],
  },
  // Coverage nav item hidden per UX review — page still accessible via direct URL
  {
    kind: "header",
    title: "API Tests",
  },
  {
    title: "Checks",
    href: "/checks",
    icon: HealthAndSafetyIcon,
  },
  {
    title: "Schedules",
    href: "/check-schedules",
    icon: ScheduleIcon,
  },
  {
    title: "Status",
    href: "/status",
    icon: MonitorHeartIcon,
  },
  {
    kind: "header",
    title: "Browsers",
  },
  {
    title: "Sessions",
    href: "/browser-sessions",
    icon: ComputerIcon,
  },
  {
    kind: "divider",
  },
  {
    title: "Settings",
    href: "/configuration",
    icon: SettingsIcon,
  },
];

function NavigationMenu({
  collapsed,
  onToggleCollapse,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { onboarding } = useOrgFlags();
  // Show the onboarding nav entry while onboarding is active OR was
  // explicitly skipped (post-skip, the user still needs a way back in).
  // Hide only on genuine completion ("auto" | "user_action") or when
  // the org is grandfathered (completed_at set with no skip marker).
  const showOnboardingEntry =
    !onboarding.completed_at || onboarding.completion_reason === "skipped";
  // Post-skip we tone the highlight down to an outlined treatment — the
  // affordance is still there but the "pushy" filled look is reserved for
  // the active (never-completed) state where we actually want the user to
  // engage with onboarding.
  const isOnboardingSkipped = onboarding.completion_reason === "skipped";
  // Filter NAVIGATION at render time: the onboarding entry disappears
  // once onboarding completes. Keeping it in the declarative NAVIGATION
  // array (rather than an ad-hoc splice in JSX) preserves relative
  // ordering and keeps the filter trivially understandable.
  const navigationItems = NAVIGATION.filter(
    (item) => item.title !== ONBOARDING_NAV_TITLE || showOnboardingEntry,
  );
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    // Defaults
    Browsers: true,
  });
  const [collapsedMenuItems, setCollapsedMenuItems] = useState<Record<string, boolean>>({});

  // Helper function to check if current URL matches a given href
  const isCurrentUrl = (href: string) => {
    const currentPath = location.pathname;
    return currentPath === href || currentPath.startsWith(`${href}/`);
  };

  // Handle navigation click
  const handleNavigation = (href: string) => {
    navigate(href);
  };

  // Toggle section collapse
  const toggleSection = (title: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Toggle menu item collapse
  const toggleMenuItem = (title: string) => {
    setCollapsedMenuItems((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        // Visible but subtle scrollbar
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: SIDEBAR.divider,
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: SIDEBAR.textInactive,
        },
        // Bottom fade to hint at more content
        maskImage: "linear-gradient(to bottom, black calc(100% - 24px), transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, black calc(100% - 24px), transparent 100%)",
      }}
    >
      <Box
        sx={{
          padding: collapsed ? "8px 0" : "16px",
          paddingBottom: collapsed ? "0" : "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            alignItems: collapsed ? "center" : "stretch",
          }}
        >
          {navigationItems.map((item, index) => {
            // Handle headers
            if (item.kind === "header") {
              if (collapsed) return null;
              const title = item.title ?? "";
              const isSectionCollapsed = collapsedSections[title];
              return (
                <Box
                  key={`header-${index}`}
                  {...clickableProps(() => toggleSection(title))}
                  onClick={() => toggleSection(title)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: SIDEBAR.textInactive,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginTop: "16px",
                    marginBottom: "8px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    cursor: "pointer",
                    "&:hover": {
                      color: SIDEBAR.textActive,
                    },
                    transition: "color 0.2s ease",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {item.title}
                  </Typography>
                  {isSectionCollapsed ? (
                    <ExpandMoreIcon sx={{ width: "16px", height: "16px" }} />
                  ) : (
                    <ExpandLessIcon sx={{ width: "16px", height: "16px" }} />
                  )}
                </Box>
              );
            }

            // Handle dividers
            if (item.kind === "divider") {
              return (
                <Box
                  key={`divider-${index}`}
                  sx={{
                    height: "1px",
                    backgroundColor: SIDEBAR.divider,
                    margin: collapsed ? "8px 0" : "16px 12px",
                    width: collapsed ? "32px" : "auto",
                  }}
                />
              );
            }

            // Check if this item is part of a collapsed section (only in expanded mode)
            if (!collapsed) {
              let currentSection = null;
              for (let i = index - 1; i >= 0; i--) {
                if (navigationItems[i].kind === "header") {
                  currentSection = navigationItems[i].title;
                  break;
                }
                if (navigationItems[i].kind === "divider") {
                  break;
                }
              }
              if (currentSection && collapsedSections[currentSection]) {
                return null;
              }
            }

            // Handle navigation items
            if (!item.icon) return null;

            const isActive = item.href ? isCurrentUrl(item.href) : false;
            const hasChildren = item.children && item.children.length > 0;
            // Only items without href but with children can collapse (like "Creation")
            const isCollapsible = hasChildren && !item.href;
            const isMenuItemCollapsed = isCollapsible && collapsedMenuItems[item.title];
            const IconComponent = item.icon;
            // Highlighted items (e.g. Onboarding while it's still relevant) use
            // BRAND_PRIMARY for the icon + bolder label, so they pop even when
            // inactive. The active state still wins over the highlight so the
            // current page stays visually dominant.
            //
            // Onboarding has two highlighted variants:
            //   - "filled"  (default)   : BRAND_PRIMARY tinted bg + bold text.
            //                              Used while onboarding is actively
            //                              pending (never completed, never
            //                              skipped) — we want the user to
            //                              engage.
            //   - "outlined" (skipped)  : transparent bg + 1px BRAND_PRIMARY
            //                              border. Quieter — the user chose
            //                              to skip, so don't nag; still
            //                              distinct from regular nav items.
            const isHighlighted = "highlight" in item && item.highlight === true;
            const isHighlightOutlined =
              isHighlighted && item.title === ONBOARDING_NAV_TITLE && isOnboardingSkipped;

            // Collapsed mode: icon-only with tooltip
            if (collapsed) {
              return (
                <Tooltip key={item.title} title={item.title} placement="right">
                  <Box
                    {...clickableProps(() => {
                      if (isCollapsible) {
                        onToggleCollapse();
                      } else if (item.href) {
                        handleNavigation(item.href);
                      }
                    })}
                    onClick={() => {
                      if (isCollapsible) {
                        onToggleCollapse();
                      } else if (item.href) {
                        handleNavigation(item.href);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      color: isActive
                        ? SIDEBAR.textActive
                        : isHighlighted
                          ? BRAND_PRIMARY
                          : SIDEBAR.textInactive,
                      backgroundColor: isActive ? SIDEBAR.itemActiveBg : "transparent",
                      "&:hover": {
                        color: SIDEBAR.textActive,
                        backgroundColor: SIDEBAR.itemActiveBg,
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <IconComponent sx={{ width: "20px", height: "20px" }} />
                  </Box>
                </Tooltip>
              );
            }

            // Expanded mode: full nav items
            return (
              <Box key={item.title}>
                {/* Parent menu item */}
                <Box
                  {...clickableProps(() => {
                    if (isCollapsible) {
                      toggleMenuItem(item.title);
                    } else if (item.href) {
                      handleNavigation(item.href);
                    }
                  })}
                  onClick={() => {
                    if (isCollapsible) {
                      toggleMenuItem(item.title);
                    } else if (item.href) {
                      handleNavigation(item.href);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    fontSize: "14px",
                    fontWeight: isHighlighted ? 600 : 500,
                    borderRadius: "6px",
                    cursor: "pointer",
                    color: isActive
                      ? SIDEBAR.textActive
                      : isHighlighted
                        ? BRAND_PRIMARY
                        : SIDEBAR.textInactive,
                    backgroundColor: isActive
                      ? SIDEBAR.itemActiveBg
                      : isHighlighted && !isHighlightOutlined
                        ? `${BRAND_PRIMARY}0D`
                        : "transparent",
                    border: isActive
                      ? `1px solid ${SIDEBAR.divider}`
                      : isHighlightOutlined
                        ? `1px solid ${BRAND_PRIMARY}`
                        : "1px solid transparent",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: SIDEBAR.textActive,
                      backgroundColor: SIDEBAR.itemActiveBg,
                    },
                  }}
                >
                  <IconComponent sx={{ marginRight: "12px", width: "16px", height: "16px" }} />
                  <Box sx={{ flex: 1 }}>{item.title}</Box>
                  {"badge" in item && item.badge && (
                    <Tooltip title="Argus is in beta — report issues via Need help?">
                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{
                          height: "18px",
                          fontSize: "10px",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          backgroundColor: BRAND_PRIMARY,
                          color: "common.white",
                          marginLeft: "6px",
                          "& .MuiChip-label": { paddingX: "6px" },
                        }}
                      />
                    </Tooltip>
                  )}
                  {isCollapsible &&
                    (isMenuItemCollapsed ? (
                      <ExpandMoreIcon sx={{ width: "16px", height: "16px" }} />
                    ) : (
                      <ExpandLessIcon sx={{ width: "16px", height: "16px" }} />
                    ))}
                </Box>

                {/* Child menu items - always shown for navigable items, conditionally for collapsible ones */}
                {hasChildren && !isMenuItemCollapsed && (
                  <Box sx={{ paddingLeft: "14px", marginTop: "4px" }}>
                    {item.children.map((child) => {
                      const isChildActive = isCurrentUrl(child.href);
                      const ChildIconComponent = child.icon;

                      return (
                        <Box
                          key={child.title}
                          {...clickableProps(() => handleNavigation(child.href))}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigation(child.href);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: "6px 12px",
                            fontSize: "13px",
                            fontWeight: 500,
                            borderRadius: "6px",
                            cursor: "pointer",
                            color: isChildActive ? SIDEBAR.textActive : SIDEBAR.textInactive,
                            backgroundColor: isChildActive ? SIDEBAR.itemActiveBg : "transparent",
                            border: isChildActive
                              ? `1px solid ${SIDEBAR.divider}`
                              : "1px solid transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              color: SIDEBAR.textActive,
                              backgroundColor: SIDEBAR.itemActiveBg,
                            },
                          }}
                        >
                          <ChildIconComponent
                            sx={{
                              marginRight: "12px",
                              width: "14px",
                              height: "14px",
                            }}
                          />
                          {child.title}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

// Reusable header component for expanded sidebar
function SidebarHeader({ onToggleCollapse }: { onToggleCollapse: () => void }) {
  return (
    <Box
      sx={{
        border: 0,
        position: "sticky",
        top: 0,
        zIndex: 30,
        padding: "8px",
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <OrganizationSwitcher
          afterSelectOrganizationUrl="/"
          afterCreateOrganizationUrl="/"
          afterLeaveOrganizationUrl="/"
          createOrganizationMode="modal"
          organizationProfileMode="modal"
          hidePersonal={false}
          appearance={{
            elements: {
              organizationSwitcherTrigger: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: SIDEBAR.divider,
                },
              },
              organizationSwitcherTriggerImage: {
                display: "none",
              },
              organizationSwitcherTriggerText: {
                display: "none",
              },
              organizationSwitcherTriggerSecondaryText: {
                display: "none",
              },
              organizationSwitcherTriggerIcon: {
                display: "none",
              },
            },
          }}
        />
        <UserButton
          afterSignOutUrl="/sign-in"
          signInUrl="/sign-in"
          appearance={{
            elements: {
              userButtonTrigger: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: SIDEBAR.divider,
                },
              },
              userButtonTriggerImage: {
                width: "24px",
                height: "24px",
                borderRadius: "4px",
              },
            },
          }}
        />
        <Tooltip title="Collapse sidebar">
          <IconButton
            onClick={onToggleCollapse}
            aria-label="Collapse sidebar"
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "6px",
              color: SIDEBAR.textInactive,
              "&:hover": {
                backgroundColor: SIDEBAR.divider,
                color: SIDEBAR.textActive,
              },
            }}
          >
            <CollapseIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// Reusable footer component for sidebar
function SidebarFooter({ collapsed }: { collapsed?: boolean }) {
  const { data } = useApiQuery<{ version: string }>("/version");
  const { toggleChat } = useSupportChatContext();

  return (
    <Box sx={{ padding: collapsed ? "8px 0" : "12px 16px", marginTop: "auto" }}>
      <Stack
        direction="column"
        spacing={collapsed ? 0.5 : 1}
        alignItems={collapsed ? "center" : "stretch"}
      >
        {/* Need help? */}
        {collapsed ? (
          <Tooltip title="Need help?" placement="right">
            <IconButton
              onClick={toggleChat}
              aria-label="Need help?"
              sx={{
                color: SIDEBAR.textInactive,
                "&:hover": {
                  backgroundColor: SIDEBAR.divider,
                  color: SIDEBAR.textActive,
                },
              }}
            >
              <LiveHelpIcon sx={{ width: "20px", height: "20px" }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Box
            {...clickableProps(toggleChat)}
            onClick={toggleChat}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "6px 10px",
              fontSize: "13px",
              fontWeight: 500,
              borderRadius: "6px",
              cursor: "pointer",
              color: SIDEBAR.textInactive,
              transition: "all 0.2s ease",
              "&:hover": {
                color: SIDEBAR.textActive,
                backgroundColor: SIDEBAR.itemActiveBg,
              },
            }}
          >
            <LiveHelpIcon sx={{ marginRight: "8px", width: "14px", height: "14px" }} />
            Need help?
          </Box>
        )}

        {/* Usage & version — only in expanded mode */}
        {!collapsed && (
          <>
            <UsageInfoStack />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-start"
              divider={
                <Typography
                  variant="caption"
                  sx={{
                    color: SIDEBAR.textInactive,
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  ·
                </Typography>
              }
            >
              {data && (
                <Tooltip title="Desplega.ai API version">
                  <Typography
                    variant="caption"
                    sx={{
                      color: SIDEBAR.textInactive,
                      fontSize: "12px",
                      lineHeight: "16px",
                      fontFamily: "monospace",
                    }}
                  >
                    <code>{data.version}</code>
                  </Typography>
                </Tooltip>
              )}
              <Tooltip title="Desplega.ai App version">
                <Typography
                  variant="caption"
                  sx={{
                    color: SIDEBAR.textInactive,
                    fontSize: "12px",
                    lineHeight: "16px",
                    fontFamily: "monospace",
                  }}
                >
                  <code>v{__APP_VERSION__}</code>
                </Typography>
              </Tooltip>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

// Mobile top bar component
function MobileTopBar({ onMenuClick }: { onMenuClick: () => void }) {
  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: SIDEBAR.bg,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        zIndex: 1200,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          minHeight: "56px",
        }}
      >
        <IconButton
          onClick={onMenuClick}
          aria-label="Open menu"
          sx={{
            color: SIDEBAR.textActive,
            "&:hover": {
              backgroundColor: SIDEBAR.divider,
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <OrganizationSwitcher
            afterSelectOrganizationUrl="/"
            afterCreateOrganizationUrl="/"
            afterLeaveOrganizationUrl="/"
            createOrganizationMode="modal"
            organizationProfileMode="modal"
            hidePersonal={false}
            appearance={{
              elements: {
                organizationSwitcherTrigger: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: SIDEBAR.divider,
                  },
                },
                organizationSwitcherTriggerImage: {
                  display: "none",
                },
                organizationSwitcherTriggerText: {
                  display: "none",
                },
                organizationSwitcherTriggerSecondaryText: {
                  display: "none",
                },
                organizationSwitcherTriggerIcon: {
                  display: "none",
                },
              },
            }}
          />
          <UserButton
            afterSignOutUrl="/sign-in"
            signInUrl="/sign-in"
            appearance={{
              elements: {
                userButtonTrigger: {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "6px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: SIDEBAR.divider,
                  },
                },
                userButtonTriggerImage: {
                  width: "24px",
                  height: "24px",
                  borderRadius: "4px",
                },
              },
            }}
          />
        </Box>
      </Box>
    </AppBar>
  );
}

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ collapsed, onToggleCollapse }: SidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // xs: 0-600px
  const isSmallScreen = useMediaQuery(theme.breakpoints.between("sm", "lg")); // sm-md: 600-1200px

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Collapsed sidebar: icon column
  const collapsedContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
        py: 1,
      }}
    >
      <OrganizationSwitcher
        afterSelectOrganizationUrl="/"
        afterCreateOrganizationUrl="/"
        afterLeaveOrganizationUrl="/"
        createOrganizationMode="modal"
        organizationProfileMode="modal"
        hidePersonal={false}
        appearance={{
          elements: {
            organizationSwitcherTrigger: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "6px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: SIDEBAR.divider,
              },
            },
            organizationSwitcherTriggerImage: {
              display: "none",
            },
            organizationSwitcherTriggerText: {
              display: "none",
            },
            organizationSwitcherTriggerSecondaryText: {
              display: "none",
            },
            organizationSwitcherTriggerIcon: {
              display: "none",
            },
          },
        }}
      />
      <Tooltip title="Expand sidebar" placement="right">
        <IconButton
          onClick={onToggleCollapse}
          aria-label="Expand sidebar"
          sx={{
            my: 0.5,
            color: SIDEBAR.textInactive,
            "&:hover": {
              backgroundColor: SIDEBAR.divider,
              color: SIDEBAR.textActive,
            },
          }}
        >
          <ExpandIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          width: "32px",
          height: "1px",
          backgroundColor: SIDEBAR.divider,
          my: 0.5,
        }}
      />
      <NavigationMenu collapsed={true} onToggleCollapse={onToggleCollapse} />
      <SidebarFooter collapsed />
    </Box>
  );

  // Expanded sidebar: full content
  const expandedContent = (
    <>
      <SidebarHeader onToggleCollapse={onToggleCollapse} />
      <NavigationMenu collapsed={false} onToggleCollapse={onToggleCollapse} />
      <SidebarFooter />
    </>
  );

  // Mobile drawer uses expanded content (no icon column on mobile)
  const drawerContent = (
    <>
      <NavigationMenu collapsed={false} onToggleCollapse={onToggleCollapse} />
      <SidebarFooter />
    </>
  );

  const sidebarContent = collapsed ? collapsedContent : expandedContent;

  // Mobile view - top bar + drawer
  if (isMobile) {
    return (
      <>
        <MobileTopBar onMenuClick={handleMobileMenuToggle} />
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "240px",
              backgroundColor: SIDEBAR.bg,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
            {...clickableProps(() => setMobileDrawerOpen(false))}
            onClick={() => setMobileDrawerOpen(false)}
          >
            {drawerContent}
          </Box>
        </Drawer>
      </>
    );
  }

  // Small screen (sm-md) - icon column when collapsed, fixed overlay when expanded
  if (isSmallScreen) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: SIDEBAR.bg,
          width: collapsed ? `${SIDEBAR_COLLAPSED_WIDTH}px` : `${SIDEBAR_EXPANDED_WIDTH}px`,
          flexShrink: 0,
          overflow: "hidden",
          transition: "width 0.3s ease-in-out",
          position: collapsed ? "relative" : "fixed",
          left: 0,
          top: 0,
          zIndex: collapsed ? "auto" : 1100,
          boxShadow: collapsed ? "none" : "2px 0 8px rgba(0,0,0,0.1)",
        }}
      >
        {sidebarContent}
      </Box>
    );
  }

  // Large screen (lg+) - normal behavior, pushes content
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: SIDEBAR.bg,
        borderRight: "0",
        width: collapsed ? `${SIDEBAR_COLLAPSED_WIDTH}px` : `${SIDEBAR_EXPANDED_WIDTH}px`,
        flexShrink: 0,
        overflow: "hidden",
        transition: "width 0.3s ease-in-out",
        position: "relative",
      }}
    >
      {sidebarContent}
    </Box>
  );
}
